const DATE_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;
const TIME_PATTERN = /^([01]\d|2[0-3]):([0-5]\d)$/;

const formatters = new Map();

function getFormatter(timeZone) {
  if (!formatters.has(timeZone)) {
    formatters.set(
      timeZone,
      new Intl.DateTimeFormat('en-CA', {
        timeZone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hourCycle: 'h23',
      })
    );
  }

  return formatters.get(timeZone);
}

function getZonedParts(timestamp, timeZone) {
  const parts = {};

  for (const part of getFormatter(timeZone).formatToParts(new Date(timestamp))) {
    if (part.type !== 'literal') parts[part.type] = Number(part.value);
  }

  return parts;
}

/**
 * Converts a wall-clock date in an IANA time zone to an absolute timestamp.
 * A second correction pass handles daylight-saving offsets without relying on
 * the visitor's local time zone.
 */
export function zonedTimeToEpoch(dateValue, timeValue, timeZone) {
  const dateMatch = DATE_PATTERN.exec(dateValue);
  const timeMatch = TIME_PATTERN.exec(timeValue);

  if (!dateMatch || !timeMatch || !timeZone) throw new Error('Invalid countdown configuration');

  const requested = {
    year: Number(dateMatch[1]),
    month: Number(dateMatch[2]),
    day: Number(dateMatch[3]),
    hour: Number(timeMatch[1]),
    minute: Number(timeMatch[2]),
    second: 0,
  };

  const requestedAsUtc = Date.UTC(
    requested.year,
    requested.month - 1,
    requested.day,
    requested.hour,
    requested.minute,
    requested.second
  );

  let candidate = requestedAsUtc;

  for (let index = 0; index < 3; index += 1) {
    const displayed = getZonedParts(candidate, timeZone);
    const displayedAsUtc = Date.UTC(
      displayed.year,
      displayed.month - 1,
      displayed.day,
      displayed.hour,
      displayed.minute,
      displayed.second
    );
    const correction = requestedAsUtc - displayedAsUtc;

    candidate += correction;
    if (correction === 0) break;
  }

  const verified = getZonedParts(candidate, timeZone);
  const isExactMatch = Object.entries(requested).every(([key, value]) => verified[key] === value);

  if (!isExactMatch) throw new Error('Invalid or non-existent local time');

  return candidate;
}

export class CountdownTimer extends HTMLElement {
  connectedCallback() {
    this.stop();
    this.values = Object.fromEntries(
      [...this.querySelectorAll('[data-countdown-value]')].map((element) => [element.dataset.countdownValue, element])
    );
    this.grid = this.querySelector('[data-countdown-grid]');
    this.error = this.querySelector('[data-countdown-error]');

    try {
      this.endTimestamp = zonedTimeToEpoch(this.dataset.endDate, this.dataset.endTime, this.dataset.timeZone);
      this.dataset.state = 'ready';
      this.tick();
    } catch (error) {
      this.showConfigurationError();
    }
  }

  disconnectedCallback() {
    this.stop();
  }

  stop() {
    if (this.timeoutId) window.clearTimeout(this.timeoutId);
    this.timeoutId = null;
  }

  tick() {
    const remainingSeconds = Math.max(0, Math.ceil((this.endTimestamp - Date.now()) / 1000));

    this.render(remainingSeconds);

    if (remainingSeconds === 0) {
      this.dataset.state = 'expired';
      this.stop();
      return;
    }

    const delayUntilNextSecond = 1000 - (Date.now() % 1000) + 15;
    this.timeoutId = window.setTimeout(() => this.tick(), delayUntilNextSecond);
  }

  render(totalSeconds) {
    const values = {
      days: Math.floor(totalSeconds / 86400),
      hours: Math.floor((totalSeconds % 86400) / 3600),
      minutes: Math.floor((totalSeconds % 3600) / 60),
      seconds: totalSeconds % 60,
    };

    for (const [unit, value] of Object.entries(values)) {
      const formattedValue = String(value).padStart(2, '0');
      if (this.values[unit]?.textContent !== formattedValue) this.values[unit].textContent = formattedValue;
    }

    const accessibleText = [...this.querySelectorAll('[data-countdown-unit]')]
      .map((unit) => {
        const value = unit.querySelector('[data-countdown-value]')?.textContent || '00';
        const label = unit.querySelector('[data-countdown-label]')?.textContent?.trim() || '';
        return `${value} ${label}`.trim();
      })
      .join(', ');

    this.setAttribute('aria-label', accessibleText);
  }

  showConfigurationError() {
    this.stop();
    this.dataset.state = 'error';

    if (this.dataset.designMode === 'true') {
      if (this.grid) this.grid.hidden = true;
      if (this.error) this.error.hidden = false;
      return;
    }

    this.hidden = true;
  }
}

if (!customElements.get('countdown-timer')) {
  customElements.define('countdown-timer', CountdownTimer);
}
